import { mutate, searchContent } from '@echo/api-client/onearly';
import { EXERCISE_MINUTE_OPTIONS } from '../domain/exercise';
import { type ContentGraph, jsonValueOf, readGraph } from './graph';
import { newIdempotencyKey } from './idempotency';

/**
 * The person's settings on their account (`SM Preferences`, scripts/seeds/README.md), so they
 * follow them from device to device. One private node per person, written as the person; today
 * it carries `exerciseMinutes`. Fields the app does not know are kept on every write.
 */
export interface AccountPreferences {
  /** The version to edit; undefined until the first save makes the node. */
  uuid: string | undefined;
  value: Record<string, unknown>;
}

export const NO_PREFERENCES: AccountPreferences = { uuid: undefined, value: {} };

/** The person's preferences, authored by them (never a node someone else shares). */
export async function loadPreferences(
  preferencesType: string,
  personUuid: string,
): Promise<AccountPreferences> {
  const response = await searchContent({
    rootContentTypeUuids: [preferencesType],
    actorPredicates: [{ edgeTypes: ['AUTHORED_BY'], actorUuids: [personUuid] }],
    maxDepth: 0,
    first: 20,
  });
  if (response.status !== 200) {
    throw new Error(`OnEarly: reading preferences failed (${response.status})`);
  }
  return preferencesFromGraph(readGraph(response.data));
}

/**
 * Two devices' first saves can each make a node; every device then keeps to the one with the
 * lowest lineage, whatever order Core lists them in, so they all read and edit the same one.
 */
export function preferencesFromGraph(graph: ContentGraph): AccountPreferences {
  const node = graph.roots
    .map((uuid) => graph.nodes.get(uuid))
    .filter((found) => found !== undefined)
    .sort((a, b) =>
      a.lineageUuid < b.lineageUuid ? -1 : a.lineageUuid > b.lineageUuid ? 1 : 0,
    )[0];
  if (!node) return NO_PREFERENCES;
  const value = jsonValueOf(node);
  return {
    uuid: node.uuid,
    value: value && typeof value === 'object' ? (value as Record<string, unknown>) : {},
  };
}

/** The stored exercise duration, when it is one the app offers. */
export function exerciseMinutesOf(preferences: AccountPreferences): number | undefined {
  const minutes = preferences.value.exerciseMinutes;
  return typeof minutes === 'number' && EXERCISE_MINUTE_OPTIONS.includes(minutes)
    ? minutes
    : undefined;
}

/** Writes the changed fields over `current`, making the node on the first save. */
export async function savePreferences(
  preferencesType: string,
  current: AccountPreferences,
  changes: Record<string, unknown>,
): Promise<AccountPreferences> {
  const value = { ...current.value, schema: 1, ...changes };
  const response = await mutate(
    current.uuid
      ? {
          idempotencyKey: newIdempotencyKey(),
          editNodes: [
            { contentUuid: current.uuid, variant: 'INSTANCE', value: { jsonValue: value } },
          ],
        }
      : {
          idempotencyKey: newIdempotencyKey(),
          addNodes: [
            {
              clientId: 'preferences',
              variant: 'INSTANCE',
              contentTypeUuid: preferencesType,
              value: { jsonValue: value },
            },
          ],
        },
  );
  if (response.status !== 200) {
    throw new Error(`OnEarly: saving preferences failed (${response.status})`);
  }
  const { clientIdMap, versionMap } = response.data.data;
  const uuid = current.uuid
    ? versionMap.find((entry) => entry.oldUuid === current.uuid)?.newUuid
    : clientIdMap.find((entry) => entry.clientId === 'preferences')?.uuid;
  if (!uuid) throw new Error('OnEarly: saving preferences returned no node');
  return { uuid, value };
}
